package com.wintermar.inspection.feature.domain.usecase.inspection

import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.feature.domain.model.inspection.SyncInspectionsResult
import com.wintermar.inspection.feature.domain.repository.InspectionRepository
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flow

/**
 * Use case for synchronizing inspections between API and local database.
 *
 * Responsibilities:
 * - Fetches latest inspections from API
 * - Compares with local inspections
 * - Identifies local-only inspections that don't exist in API
 * - Returns list of inspection IDs to be removed
 */
class SyncInspectionUseCase(
    private val inspectionRepository: InspectionRepository
) {
    /**
     * Executes the sync operation.
     * @param vesselList List of vessels to sync inspections for
     * @return Flow of sync results containing removed inspection IDs
     */
    operator fun invoke(): Flow<FlowResult<SyncInspectionsResult>> = flow {
        emit(FlowResult.Loading(true))

        // Step 1: Get all InspectionModel from API
        val apiResult = inspectionRepository.getInspections(
            vesselList = emptyList(),
            page = 1,
            pageSize = Constant.ALL_PAGE_SIZE
        ).first { it !is FlowResult.Loading }

        if (apiResult is FlowResult.Error) {
            emit(FlowResult.Loading(false))
            emit(FlowResult.Error(apiResult.error))
            return@flow
        }

        // Extract List<InspectionModel> from API result
        val apiInspections = (apiResult as FlowResult.Success).data?.data.orEmpty()

        // Step 2: Get all InspectionModel from local DB
        val localResult = inspectionRepository.getInspectionsFromLocalDb(
            page = 1,
            pageSize = Constant.ALL_PAGE_SIZE
        ).first { it !is FlowResult.Loading }

        if (localResult is FlowResult.Error) {
            emit(FlowResult.Loading(false))
            emit(FlowResult.Error(localResult.error))
            return@flow
        }

        // Extract List<InspectionModel> from local DB result
        val localInspections = if (localResult is FlowResult.Success) {
            localResult.data?.data.orEmpty()
        } else {
            emptyList()
        }

        // Step 3: Compare and identify local-only inspections
        val apiInspectionIds = apiInspections.map { it.id }.toSet()
        val localInspectionIds = localInspections.map { it.id }.toSet()

        // Find inspections that exist in local DB but NOT in API
        val removedInspectionIds = (localInspectionIds - apiInspectionIds).toList()

        emit(FlowResult.Loading(false))
        emit(FlowResult.Success(SyncInspectionsResult(removedInspectionIds)))
    }
}
